import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo, { faqSchema, breadcrumbSchema } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check } from "lucide-react";

const FEATURES = [
  "Unlimited caregiver search",
  "Unlimited direct messaging",
  "Save favourite caregivers",
  "Booking requests & availability calendar",
  "AI-powered caregiver matching",
  "Priority support",
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const startCheckout = async () => {
    if (!user) { nav("/register"); return; }
    if (user.role !== "parent") { toast.error("Only parent accounts can subscribe to Premium."); return; }
    if (user.is_premium) { toast.info("You're already Premium!"); nav("/dashboard"); return; }
    try {
      const { data } = await api.post("/subscription/checkout", {
        lookup_key: "sakeenah_premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel your Premium membership? You'll return to Free immediately.")) return;
    try { await api.post("/subscription/cancel"); await refresh(); toast.success("Membership cancelled"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-linen">
      <Seo
        title="Membership pricing — Free & Premium at $9.99/mo"
        description="Free for caregivers, always. Parents upgrade to Premium ($9.99/mo) for unlimited caregiver search, messaging, favourites, bookings and AI matching."
        path="/pricing"
        jsonLd={[
          breadcrumbSchema([{ name: "Pricing", path: "/pricing" }]),
          faqSchema([
            { q: "How much is Premium?", a: "$9.99 per month. Unlimited caregiver search, messaging, favourites, bookings, availability calendar, AI matching and priority support. Cancel any time." },
            { q: "Do caregivers pay to join?", a: "No — caregivers join Sakeenah Circle completely free." },
            { q: "Can I cancel any time?", a: "Yes. You keep Premium until the end of your billing period, then return to Free automatically." },
          ]),
        ]}
      />
      <Nav />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-8 text-center">
        <span className="badge-gold"><Sparkles className="w-3 h-3" /> Membership</span>
        <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-3 leading-tight tracking-tight">One plan. Full peace of mind.</h1>
        <p className="text-forest/70 mt-4 max-w-xl mx-auto">Free for caregivers, always. Parents upgrade once — get everything.</p>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-16 grid md:grid-cols-2 gap-6">
        <div className="card-organic p-8 md:p-10" data-testid="pricing-free">
          <div className="text-xs uppercase tracking-[0.15em] text-forest/60">Parent · Free</div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-serif text-5xl md:text-6xl text-forest">$0</span>
            <span className="text-sm text-forest/60">/ forever</span>
          </div>
          <p className="text-forest/70 mt-4">Explore the circle. Great for research.</p>
          <ul className="mt-6 space-y-3 text-sm text-forest/80">
            <li className="flex gap-2"><Check className="w-4 h-4 text-forest mt-0.5" /> Browse a curated selection of caregivers</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-forest mt-0.5" /> See verifications & languages</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-forest mt-0.5" /> Read family reviews</li>
          </ul>
          {!user && <Link to="/register" className="btn-outline w-full block text-center mt-8" data-testid="pricing-free-cta">Create free account</Link>}
          {user && !user.is_premium && <div className="text-sm text-forest/50 mt-8 text-center">You're on the Free plan.</div>}
        </div>
        <div className="card-organic p-8 md:p-10 relative overflow-hidden border-forest/40" data-testid="pricing-premium">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gold/25 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.15em] text-forest/60">Parent · Premium</span>
              <span className="badge-gold"><Sparkles className="w-3 h-3" /> Best value</span>
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif text-5xl md:text-6xl text-forest">$9.99</span>
              <span className="text-sm text-forest/60">/ month</span>
            </div>
            <p className="text-forest/70 mt-4">Unlock the full Sakeenah Circle.</p>
            <ul className="mt-6 space-y-3 text-sm text-forest/85">
              {FEATURES.map((f) => (
                <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5" /> {f}</li>
              ))}
            </ul>
            {user?.is_premium ? (
              <button onClick={cancel} className="btn-outline w-full mt-8" data-testid="pricing-cancel">Cancel membership</button>
            ) : (
              <button onClick={startCheckout} className="btn-primary w-full mt-8" data-testid="pricing-premium-cta">
                {user ? "Upgrade to Premium" : "Get started with Premium"}
              </button>
            )}
            <div className="text-[11px] text-forest/50 text-center mt-3">Cancel any time · No lock-in · Stripe-secured</div>
          </div>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="card-organic p-6 md:p-8 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="font-serif text-2xl text-forest">Are you a caregiver?</div>
            <div className="text-forest/70 mt-1 text-sm">Join Sakeenah completely free — build your profile and start receiving booking requests.</div>
          </div>
          <Link to="/register?role=babysitter" className="btn-outline" data-testid="become-caregiver">Become a caregiver</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
